import type { ObjectMetadata } from '../../data/client';

/**
 * THE SIDEBAR'S SECTIONS (record-page-and-views.md D11, D12), as a pure function.
 *
 * The navigation was one flat, unsorted run of every object - fourteen entries in array
 * order, with "Agency members" second because that is where the metadata happened to put
 * it. Grouping turns it into Clients / Sales / Delivery / Team / System, which is the
 * order somebody actually works in.
 *
 * GROUPING IS PRESENTATION, NEVER VISIBILITY, and that sentence is load-bearing: the
 * admin module already tabulates three ways an object can fail to appear (hidden,
 * archived, not in this view), and making "no group" mean "not in the navigation" would
 * add a fourth. An ungrouped object renders in a TRAILING UNLABELLED section, exactly as
 * every object did before this existed.
 *
 * A GROUP RANKS WHERE ITS FIRST MEMBER SITS - D9's rule for field groups, applied one
 * level up. So re-ordering the sidebar is re-ordering objects, which the admin module's
 * move buttons already do; there is no second stored number to keep in step with
 * `position`, and no alphabetical accident deciding that Delivery comes before Sales.
 */
export interface ObjectNavGroup {
  /** The group label, or `null` for the trailing unlabelled section. */
  readonly label: string | null;
  readonly objects: readonly ObjectMetadata[];
}

export function objectNavGroups(objects: readonly ObjectMetadata[]): readonly ObjectNavGroup[] {
  const ordered = [...objects].toSorted((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const grouped = new Map<string, ObjectMetadata[]>();
  const ungrouped: ObjectMetadata[] = [];

  for (const object of ordered) {
    const label = normalise(object.navGroup);
    if (label === null) {
      ungrouped.push(object);
      continue;
    }
    const bucket = grouped.get(label);
    // Insertion order IS first-member order, because `ordered` is sorted by position.
    if (bucket === undefined) grouped.set(label, [object]);
    else bucket.push(object);
  }

  const groups: ObjectNavGroup[] = [...grouped].map(([label, members]) => ({
    label,
    objects: members,
  }));
  /**
   * Ungrouped LAST, for the reason the field groups have: an object somebody created
   * five minutes ago and has not filed yet must not push the organised ones down. It is
   * also what makes an instance where nobody has grouped anything render exactly as it
   * did before - one unlabelled run, unchanged.
   */
  if (ungrouped.length > 0) groups.push({ label: null, objects: ungrouped });
  return groups;
}

/** `nav_group` is free text and will be typo'd (open risk 5); trimming is the mitigation. */
function normalise(label: string | undefined): string | null {
  if (label === undefined) return null;
  const trimmed = label.trim();
  return trimmed === '' ? null : trimmed;
}
